/* eslint-disable @typescript-eslint/no-use-before-define */ import { getScrollParent } from './getScrollParent';
/**
 * Scrolls `scrollView` so that `element` is visible.
 * Similar to `element.scrollIntoView({block: 'nearest'})` (not supported in Edge),
 * but doesn't affect parents above `scrollView`.
 */ export function scrollIntoView(scrollView, element) {
    const offsetX = relativeOffset(scrollView, element, 'left');
    const offsetY = relativeOffset(scrollView, element, 'top');
    const width = element.offsetWidth;
    const height = element.offsetHeight;
    let x = scrollView.scrollLeft;
    let y = scrollView.scrollTop;
    // Account for top/left border offsetting the scroll top/Left
    const { borderTopWidth, borderLeftWidth } = getComputedStyle(scrollView);
    const borderAdjustedX = scrollView.scrollLeft + parseInt(borderLeftWidth, 10);
    const borderAdjustedY = scrollView.scrollTop + parseInt(borderTopWidth, 10);
    // Ignore end/bottom border via clientHeight/Width instead of offsetHeight/Width
    const maxX = borderAdjustedX + scrollView.clientWidth;
    const maxY = borderAdjustedY + scrollView.clientHeight;
    if (offsetX <= x) {
        x = offsetX - parseInt(borderLeftWidth, 10);
    } else if (offsetX + width > maxX) {
        x += offsetX + width - maxX;
    }
    if (offsetY <= borderAdjustedY) {
        y = offsetY - parseInt(borderTopWidth, 10);
    } else if (offsetY + height > maxY) {
        y += offsetY + height - maxY;
    }
    scrollView.scrollLeft = x;
    scrollView.scrollTop = y;
}
/**
 * Computes the offset left or top from child to ancestor by accumulating
 * offsetLeft or offsetTop through intervening offsetParents.
 */ function relativeOffset(ancestor, child, axis) {
    const prop = axis === 'left' ? 'offsetLeft' : 'offsetTop';
    let sum = 0;
    let currentChild = child;
    while(currentChild.offsetParent){
        sum += currentChild[prop];
        if (currentChild.offsetParent === ancestor) {
            break;
        } else if (currentChild.offsetParent.contains(ancestor)) {
            // If the ancestor is not `position:relative`, then we stop at
            // _its_ offset parent, and we subtract off _its_ offset, so that
            // we end up with the proper offset from child to ancestor.
            sum -= ancestor[prop];
            break;
        }
        currentChild = currentChild.offsetParent;
    }
    return sum;
}
/**
 * Scrolls the `targetElement` so it is visible in the viewport. Accepts an optional `opts.containingElement`
 * that will be centered in the viewport prior to scrolling the targetElement into view. If scrolling is prevented on
 * the body (e.g. targetElement is in a popover), this will only scroll the scroll parents of the targetElement up to but not including the body itself.
 */ export function scrollIntoViewport(targetElement, opts) {
    let currentTargetElement = targetElement;
    if (document.contains(currentTargetElement)) {
        const root = document.scrollingElement || document.documentElement;
        const isScrollPrevented = window.getComputedStyle(root).overflow === 'hidden';
        // If scrolling is not currently prevented then we aren’t in a overlay nor is a overlay open, just use element.scrollIntoView to bring the element into view
        if (!isScrollPrevented) {
            var // use scrollIntoView({block: 'nearest'}) instead of .focus to check if the element is fully in view or not since .focus()
            // won't cause a scroll if the element is already focused and doesn't behave consistently when an element is partially out of view horizontally vs vertically
            _currentTargetElement_scrollIntoView;
            const { left: originalLeft, top: originalTop } = currentTargetElement.getBoundingClientRect();
            currentTargetElement === null || currentTargetElement === void 0 ? void 0 : (_currentTargetElement_scrollIntoView = currentTargetElement.scrollIntoView) === null || _currentTargetElement_scrollIntoView === void 0 ? void 0 : _currentTargetElement_scrollIntoView.call(currentTargetElement, {
                block: 'nearest'
            });
            const { left: newLeft, top: newTop } = currentTargetElement.getBoundingClientRect();
            // Account for sub pixel differences from rounding
            if (Math.abs(originalLeft - newLeft) > 1 || Math.abs(originalTop - newTop) > 1) {
                var _opts_containingElement_scrollIntoView, _opts_containingElement, _currentTargetElement_scrollIntoView1;
                opts === null || opts === void 0 ? void 0 : (_opts_containingElement = opts.containingElement) === null || _opts_containingElement === void 0 ? void 0 : (_opts_containingElement_scrollIntoView = _opts_containingElement.scrollIntoView) === null || _opts_containingElement_scrollIntoView === void 0 ? void 0 : _opts_containingElement_scrollIntoView.call(_opts_containingElement, {
                    block: 'center',
                    inline: 'center'
                });
                (_currentTargetElement_scrollIntoView1 = currentTargetElement.scrollIntoView) === null || _currentTargetElement_scrollIntoView1 === void 0 ? void 0 : _currentTargetElement_scrollIntoView1.call(currentTargetElement, {
                    block: 'nearest'
                });
            }
        } else {
            let scrollParent = getScrollParent(currentTargetElement);
            // If scrolling is prevented, we don't want to scroll the body since it might move the overlay partially offscreen and the user can't scroll it back into view.
            while(currentTargetElement && scrollParent && currentTargetElement !== root && scrollParent !== root){
                scrollIntoView(scrollParent, currentTargetElement);
                currentTargetElement = scrollParent;
                scrollParent = getScrollParent(currentTargetElement);
            }
        }
    }
}

//# sourceMappingURL=scrollIntoView.js.map